// /src/pages/TR9Page.js
//
// TR-9 (Michigan Scrap Vehicle Inventory) generator.
//
// The state supplies the TR-9 as pre-printed, pre-numbered stock, so this page
// draws none of the form: it lays out only the values, positioned to land on
// the printed rules when a blank form is fed through the printer. Every
// coordinate below is in inches on an 11 x 8.5 landscape page, measured off a
// 220 dpi scan of a blank form (docs/TR-9 Blank.docx) by detecting the printed
// rules rather than by eye.
//
// The spacing is trustworthy -- the ten row rules are evenly pitched to within
// a pixel of the fitted step. The absolute registration is not: the scan runs
// to paper on all four edges, so it is a crop of the sheet by some unknown
// margin, and no printer agrees on where the page starts anyway. That is what
// the calibration nudge is for. It is expected to be used once per printer,
// against a real blank form, and is then remembered per browser.

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";

const API_BASE = "/api";

// The form has exactly ten numbered vehicle lines. The backend reports this
// too; this is the fallback if the context call has not landed yet.
const ROWS_PER_FORM = 10;

const PAGE = { w: 11.0, h: 8.5 };

// Values sit just above the rule they are written on, the way a typewriter
// would have set them.
const SIT_ABOVE = 0.045;

// --- Measured field geometry -----------------------------------------------
// x is where the value starts, w is how much room it has before it would run
// into the next field or off the end of the rule.

const SELLER_FIELDS = {
  name:    { x: 1.60, y: 1.426, w: 3.40 },
  street:  { x: 1.60, y: 1.736, w: 3.40 },
  city:    { x: 1.60, y: 2.050, w: 2.70 },
  zip:     { x: 4.45, y: 2.050, w: 0.58 },
  license: { x: 1.96, y: 2.357, w: 3.04 },
};

const PURCHASER_FIELDS = {
  name:    { x: 7.03, y: 1.394, w: 2.30 },
  date:    { x: 9.96, y: 1.399, w: 0.98 },
  street:  { x: 7.03, y: 1.713, w: 3.88 },
  city:    { x: 7.03, y: 2.028, w: 3.12 },
  zip:     { x: 10.25, y: 2.028, w: 0.70 },
  license: { x: 7.54, y: 2.348, w: 3.40 },
};

// Row 1's rule, and the spacing to the next -- fitted across all ten detected
// rules so a little skew in the scan does not accumulate down the page.
const ROW_ONE_Y = 3.6383;
const ROW_STEP = 0.31591;

const VEHICLE_COLUMNS = [
  { key: "year",         x: 0.51,  w: 1.05 },
  { key: "make",         x: 1.85,  w: 1.38 },
  { key: "vin",          x: 3.50,  w: 2.21, mono: true },
  { key: "title_number", x: 6.00,  w: 1.58 },
  { key: "stock_number", x: 7.87,  w: 2.10 },
  { key: "color",        x: 10.26, w: 0.70 },
];

const BASE_PT = 10.5;

const DEFAULT_CALIBRATION = { dx: 0, dy: 0, scale: 100 };
const CALIBRATION_KEY = "tr9.calibration";

// --- helpers ---------------------------------------------------------------

const str = (v) => (v === null || v === undefined ? "" : String(v).trim());

const SANS_STACK = 'Arial, Helvetica, sans-serif';
const MONO_STACK = '"Courier New", Courier, monospace';

// One offscreen canvas, reused: text measurement is called for every value on
// every sheet, and creating a canvas per call is what makes that slow.
let measureCtx = null;
function getMeasureCtx() {
  if (!measureCtx && typeof document !== "undefined") {
    measureCtx = document.createElement("canvas").getContext("2d");
  }
  return measureCtx;
}

/**
 * Shrink a value that would overrun its rule, instead of letting it collide
 * with the next field or get clipped by the edge of the sheet.
 *
 * The width is measured rather than estimated from the character count. These
 * fields carry MAKE, COLOR and VIN in capitals, and capitals are far wider per
 * character than the mixed-case average -- estimating cost us the tail of
 * "DARK BLUE", which on a filed state form is a wrong answer, not a cosmetic
 * one.
 */
function fitPt(text, widthIn, mono) {
  const value = str(text);
  if (!value) return BASE_PT;

  const ctx = getMeasureCtx();
  if (!ctx) return BASE_PT; // no DOM to measure against; nothing is rendering either

  ctx.font = `${mono ? "700 " : ""}${BASE_PT}pt ${mono ? MONO_STACK : SANS_STACK}`;
  const measured = ctx.measureText(value).width; // CSS px at BASE_PT
  const allowed = widthIn * 96 * 0.98;           // hairline margin against rounding

  if (measured <= allowed) return BASE_PT;
  return Math.max(5, BASE_PT * (allowed / measured));
}

function formatDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${m}/${d}/${y}`;
}

function todayISO() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function loadCalibration() {
  try {
    const raw = localStorage.getItem(CALIBRATION_KEY);
    if (!raw) return DEFAULT_CALIBRATION;
    const parsed = JSON.parse(raw);
    return {
      dx: Number(parsed.dx) || 0,
      dy: Number(parsed.dy) || 0,
      scale: Number(parsed.scale) || 100,
    };
  } catch {
    return DEFAULT_CALIBRATION;
  }
}

// --- print stylesheet ------------------------------------------------------
// Scoped to this page by being mounted and unmounted with it. The @page and
// body rules have to be global to work at all, which is why they live here
// rather than in a class on our own markup.

const PRINT_CSS = `
#tr9-print-portal { display: none; }

.tr9-sheet {
  position: relative;
  width: ${PAGE.w}in;
  height: ${PAGE.h}in;
  background: #fff;
  overflow: hidden;
}
.tr9-ink {
  position: absolute;
  color: #000;
  font-family: ${SANS_STACK};
  line-height: 1;
  white-space: nowrap;
}
.tr9-ink.mono {
  font-family: ${MONO_STACK};
  font-weight: 700;
}
.tr9-guide {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: fill;
  pointer-events: none;
}
.tr9-preview-frame {
  border: 1px solid #dee2e6;
  box-shadow: 0 1px 4px rgba(0,0,0,.12);
  overflow: hidden;
  background: #fff;
}

@media print {
  @page { size: ${PAGE.w}in ${PAGE.h}in; margin: 0; }
  html, body {
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
  }
  /* Everything the app renders lives under #root; the sheets are portalled to
     their own body child so they can be the only thing on the paper. */
  body > *:not(#tr9-print-portal) { display: none !important; }
  #tr9-print-portal { display: block !important; }
  .tr9-guide { display: none !important; }
  .tr9-sheet { break-after: page; page-break-after: always; }
  .tr9-sheet:last-child { break-after: auto; page-break-after: auto; }
}
`;

// --- one printed sheet -----------------------------------------------------

function Ink({ field, value, mono }) {
  const text = str(value);
  if (!text) return null;
  return (
    <div
      className={`tr9-ink${mono ? " mono" : ""}`}
      style={{
        left: `${field.x}in`,
        bottom: `${PAGE.h - field.y + SIT_ABOVE}in`,
        maxWidth: `${field.w}in`,
        fontSize: `${fitPt(text, field.w, mono)}pt`,
      }}
    >
      {text}
    </div>
  );
}

function Tr9Sheet({ form, seller, purchaser, conveyDate, calibration, guide }) {
  const shift = {
    transform:
      `translate(${calibration.dx / 25.4}in, ${calibration.dy / 25.4}in) ` +
      `scale(${(calibration.scale || 100) / 100})`,
    transformOrigin: "top left",
    position: "absolute",
    inset: 0,
  };

  return (
    <div className="tr9-sheet">
      {guide && (
        <img className="tr9-guide" src="/tr9-guide.jpg" alt="" aria-hidden="true" />
      )}
      <div style={shift}>
        <Ink field={SELLER_FIELDS.name} value={seller?.name} />
        <Ink field={SELLER_FIELDS.street} value={seller?.street} />
        <Ink field={SELLER_FIELDS.city} value={seller?.city} />
        <Ink field={SELLER_FIELDS.zip} value={seller?.zip} />
        <Ink field={SELLER_FIELDS.license} value={seller?.license_number} />

        <Ink field={PURCHASER_FIELDS.name} value={purchaser?.name} />
        <Ink field={PURCHASER_FIELDS.date} value={formatDate(conveyDate)} />
        <Ink field={PURCHASER_FIELDS.street} value={purchaser?.street} />
        <Ink field={PURCHASER_FIELDS.city} value={purchaser?.city} />
        <Ink field={PURCHASER_FIELDS.zip} value={purchaser?.zip} />
        <Ink field={PURCHASER_FIELDS.license} value={purchaser?.license_number} />

        {form.map((vehicle, i) =>
          VEHICLE_COLUMNS.map((col) => (
            <Ink
              key={`${i}-${col.key}`}
              field={{
                x: col.x,
                y: ROW_ONE_Y + ROW_STEP * i,
                w: col.w,
              }}
              value={vehicle[col.key]}
              mono={col.mono}
            />
          ))
        )}
      </div>
    </div>
  );
}

// --- page ------------------------------------------------------------------

export default function TR9Page() {
  const { token } = useContext(AuthContext);

  const [context, setContext] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set());

  const [purchaserId, setPurchaserId] = useState("");
  const [conveyDate, setConveyDate] = useState(todayISO());
  const [copies, setCopies] = useState(3);

  const [calibration, setCalibration] = useState(loadCalibration);
  const [showGuide, setShowGuide] = useState(true);
  const [forms, setForms] = useState(null);

  const previewRef = useRef(null);
  const [previewZoom, setPreviewZoom] = useState(0.55);

  const authHeaders = useMemo(
    () => ({
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }),
    [token]
  );

  // The portal node: a body-level sibling of #root, so print CSS can hide the
  // whole application and leave the sheets standing.
  const portalNode = useMemo(() => {
    if (typeof document === "undefined") return null;
    const existing = document.getElementById("tr9-print-portal");
    if (existing) return existing;
    const node = document.createElement("div");
    node.id = "tr9-print-portal";
    return node;
  }, []);

  useEffect(() => {
    if (!portalNode) return undefined;
    document.body.appendChild(portalNode);
    return () => {
      if (portalNode.parentNode) portalNode.parentNode.removeChild(portalNode);
    };
  }, [portalNode]);

  useEffect(() => {
    const style = document.createElement("style");
    style.setAttribute("data-tr9", "print");
    style.textContent = PRINT_CSS;
    document.head.appendChild(style);
    return () => {
      if (style.parentNode) style.parentNode.removeChild(style);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(CALIBRATION_KEY, JSON.stringify(calibration));
  }, [calibration]);

  // Scale the on-screen preview to whatever width the column gives us. An
  // 11in sheet is 1056 CSS pixels, which no laptop shows at full size.
  useEffect(() => {
    const measure = () => {
      const el = previewRef.current;
      if (!el) return;
      setPreviewZoom(Math.min(1, el.clientWidth / (PAGE.w * 96)));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [forms]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setBusy(true);
      setErr("");
      try {
        const [ctxRes, vehRes] = await Promise.all([
          fetch(`${API_BASE}/tr9/context`, { headers: authHeaders }),
          fetch(`${API_BASE}/tr9/vehicles`, { headers: authHeaders }),
        ]);

        const ctxBody = await ctxRes.json().catch(() => ({}));
        if (!ctxRes.ok) throw new Error(ctxBody.error || `HTTP ${ctxRes.status}`);

        const vehBody = await vehRes.json().catch(() => ({}));
        if (!vehRes.ok) throw new Error(vehBody.error || `HTTP ${vehRes.status}`);

        if (cancelled) return;
        setContext(ctxBody);
        setVehicles(vehBody.vehicles || []);

        // Vehicles nearly always go to the same yard, so that site's configured
        // destination is pre-selected. The server only sends one it has checked
        // is still selectable, so this can never silently pre-fill a yard that
        // has since been barred from receiving.
        const preferred = (ctxBody.purchasers || []).find(
          (p) => p.location === ctxBody.default_purchaser
        );
        if (preferred) setPurchaserId(String(preferred.id));
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Failed to load TR-9 data.");
      } finally {
        if (!cancelled) setBusy(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [authHeaders]);

  const rowsPerForm = context?.rows_per_form || ROWS_PER_FORM;
  const seller = context?.seller;
  const purchasers = context?.purchasers || [];
  const purchaser = purchasers.find((p) => String(p.id) === purchaserId) || null;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return vehicles;
    return vehicles.filter((v) =>
      ["year", "make", "model", "vin", "title_number", "stock_number", "color"].some(
        (k) => str(v[k]).toLowerCase().includes(q)
      )
    );
  }, [vehicles, search]);

  // Selection is kept in the table's own order, which is stock-number order --
  // the form requires titles to be attached in the order the vehicles are
  // listed, so what a user stacks on the desk matches what prints.
  const selectedVehicles = useMemo(
    () => vehicles.filter((v) => selected.has(v.title_id)),
    [vehicles, selected]
  );

  const toggle = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((v) => selected.has(v.title_id));

  const toggleAllFiltered = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) filtered.forEach((v) => next.delete(v.title_id));
      else filtered.forEach((v) => next.add(v.title_id));
      return next;
    });
  };

  const formCount = Math.ceil(selectedVehicles.length / rowsPerForm) || 0;

  const handleGenerate = () => {
    if (!selectedVehicles.length || !purchaser) return;
    const chunks = [];
    for (let i = 0; i < selectedVehicles.length; i += rowsPerForm) {
      chunks.push(selectedVehicles.slice(i, i + rowsPerForm));
    }
    setForms(chunks);
    // Let the preview mount before we scroll to it.
    window.requestAnimationFrame(() => {
      previewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const nudge = (key, delta) =>
    setCalibration((c) => ({ ...c, [key]: Math.round((c[key] + delta) * 10) / 10 }));

  // ---- render -------------------------------------------------------------

  const sheetsForPrint = [];
  if (forms) {
    forms.forEach((form, f) => {
      // Copies stay grouped with their form so a set does not get shuffled.
      for (let c = 0; c < copies; c += 1) {
        sheetsForPrint.push(
          <Tr9Sheet
            key={`${f}-${c}`}
            form={form}
            seller={seller}
            purchaser={purchaser}
            conveyDate={conveyDate}
            calibration={calibration}
            guide={false}
          />
        );
      }
    });
  }

  return (
    <Container fluid className="mt-4">
      <Row>
        <Col>
          <Card className="mb-3">
            <Card.Header>
              <div className="d-flex flex-wrap gap-3 align-items-center">
                <Link to="/titleagent" className="btn btn-outline-secondary btn-sm">
                  ← Back
                </Link>
                <div className="fw-bold">Generate TR-9</div>
                <small className="text-muted">Scrap Vehicle Inventory</small>
                {context?.location && (
                  <Badge bg="secondary">{context.location}</Badge>
                )}
                <div className="ms-auto d-flex gap-2 align-items-center">
                  <Badge bg={selected.size ? "primary" : "light"} text={selected.size ? "light" : "dark"}>
                    {selected.size} selected
                  </Badge>
                  {formCount > 1 && (
                    <Badge bg="warning" text="dark">{formCount} forms</Badge>
                  )}
                </div>
              </div>
            </Card.Header>

            <Card.Body>
              {err && <Alert variant="danger">{err}</Alert>}
              {busy ? (
                <div className="d-flex justify-content-center p-4">
                  <Spinner animation="border" />
                </div>
              ) : (
                <>
                  <Row className="g-3 align-items-end">
                    <Col md={3}>
                      <Form.Label className="small text-muted mb-1">
                        Seller (this site)
                      </Form.Label>
                      <div className="small">
                        <div className="fw-semibold">{str(seller?.name) || "—"}</div>
                        <div>{str(seller?.street)}</div>
                        <div>
                          {str(seller?.city)} {str(seller?.state)} {str(seller?.zip)}
                        </div>
                        <div className="text-muted">
                          Lic. {str(seller?.license_number) || "—"}
                        </div>
                      </div>
                    </Col>

                    <Col md={4}>
                      <Form.Label className="small text-muted mb-1">
                        Purchaser — conveyed to
                      </Form.Label>
                      <Form.Select
                        size="sm"
                        value={purchaserId}
                        onChange={(e) => setPurchaserId(e.target.value)}
                      >
                        <option value="">Select the yard being conveyed to…</option>
                        {purchasers.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.location}
                            {p.name ? ` — ${p.name}` : ""}
                          </option>
                        ))}
                      </Form.Select>
                      {purchaser && (
                        <div className="small text-muted mt-1">
                          {str(purchaser.street)}
                          {purchaser.street ? ", " : ""}
                          {str(purchaser.city)} {str(purchaser.state)}{" "}
                          {str(purchaser.zip)} · Lic.{" "}
                          {str(purchaser.license_number) || "—"}
                        </div>
                      )}
                    </Col>

                    <Col md={2}>
                      <Form.Label className="small text-muted mb-1">Date</Form.Label>
                      <Form.Control
                        size="sm"
                        type="date"
                        value={conveyDate}
                        onChange={(e) => setConveyDate(e.target.value)}
                      />
                    </Col>

                    <Col md={1}>
                      <Form.Label className="small text-muted mb-1">Copies</Form.Label>
                      <Form.Select
                        size="sm"
                        value={copies}
                        onChange={(e) => setCopies(Number(e.target.value))}
                      >
                        <option value={1}>1</option>
                        <option value={2}>2</option>
                        <option value={3}>3</option>
                      </Form.Select>
                    </Col>

                    <Col md={2}>
                      <Button
                        variant="primary"
                        className="w-100"
                        disabled={!selectedVehicles.length || !purchaser}
                        onClick={handleGenerate}
                      >
                        Generate TR-9
                      </Button>
                    </Col>
                  </Row>

                  {!purchasers.length && (
                    <Alert variant="warning" className="mt-3 mb-0 py-2 small">
                      There is nowhere for {context?.location} to convey to — no
                      other site is set up to receive transfers. An administrator
                      can add or enable one under{" "}
                      <Link to="/titleagent/admin">Title Agent settings</Link>.
                    </Alert>
                  )}
                </>
              )}
            </Card.Body>
          </Card>

          {/* Vehicles at this site */}
          <Card className="mb-3">
            <Card.Header className="d-flex flex-wrap gap-3 align-items-center">
              <div className="fw-bold">Vehicles at {context?.location || "this site"}</div>
              <Form.Control
                size="sm"
                style={{ maxWidth: 260 }}
                placeholder="Search VIN, make, stock no…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <Form.Check
                type="checkbox"
                id="tr9-select-all"
                label="Select all shown"
                checked={allFilteredSelected}
                onChange={toggleAllFiltered}
                disabled={!filtered.length}
              />
              {selected.size > 0 && (
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => setSelected(new Set())}
                >
                  Clear
                </Button>
              )}
              <div className="ms-auto small text-muted">
                Titles must be attached in the order listed below.
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {!busy && filtered.length === 0 ? (
                <div className="p-3 text-muted">No vehicles found.</div>
              ) : (
                <div style={{ maxHeight: "50vh", overflow: "auto" }}>
                  <table className="table table-sm table-hover mb-0">
                    <thead className="table-light position-sticky top-0">
                      <tr>
                        <th style={{ width: 40 }} />
                        <th className="text-nowrap">Stock No</th>
                        <th className="text-nowrap">Year</th>
                        <th className="text-nowrap">Make</th>
                        <th className="text-nowrap">VIN</th>
                        <th className="text-nowrap">Title No</th>
                        <th className="text-nowrap">Color</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((v) => (
                        <tr
                          key={v.title_id}
                          className={selected.has(v.title_id) ? "table-primary" : ""}
                          onClick={() => toggle(v.title_id)}
                          style={{ cursor: "pointer" }}
                        >
                          <td className="align-middle">
                            <Form.Check
                              type="checkbox"
                              checked={selected.has(v.title_id)}
                              onChange={() => toggle(v.title_id)}
                              onClick={(e) => e.stopPropagation()}
                              aria-label={`Select ${str(v.vin) || v.title_id}`}
                            />
                          </td>
                          <td className="text-nowrap align-middle">{str(v.stock_number)}</td>
                          <td className="text-nowrap align-middle">{str(v.year)}</td>
                          <td className="text-nowrap align-middle">{str(v.make)}</td>
                          <td className="text-nowrap align-middle font-monospace small">
                            {str(v.vin)}
                          </td>
                          <td className="text-nowrap align-middle">{str(v.title_number)}</td>
                          <td className="text-nowrap align-middle">{str(v.color)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Preview + print */}
          {forms && (
            <Card className="mb-4">
              <Card.Header className="d-flex flex-wrap gap-3 align-items-center">
                <div className="fw-bold">
                  Preview — {forms.length} form{forms.length === 1 ? "" : "s"} ×{" "}
                  {copies} cop{copies === 1 ? "y" : "ies"} ={" "}
                  {forms.length * copies} sheet
                  {forms.length * copies === 1 ? "" : "s"}
                </div>
                <Form.Check
                  type="switch"
                  id="tr9-guide-switch"
                  label="Show blank form behind (screen only)"
                  checked={showGuide}
                  onChange={(e) => setShowGuide(e.target.checked)}
                />
                <div className="ms-auto d-flex gap-2 align-items-center">
                  <span className="small text-muted">Nudge (mm)</span>
                  <div className="btn-group btn-group-sm">
                    <Button variant="outline-secondary" onClick={() => nudge("dx", -0.5)}>←</Button>
                    <Button variant="outline-secondary" onClick={() => nudge("dx", 0.5)}>→</Button>
                    <Button variant="outline-secondary" onClick={() => nudge("dy", -0.5)}>↑</Button>
                    <Button variant="outline-secondary" onClick={() => nudge("dy", 0.5)}>↓</Button>
                  </div>
                  <span className="small text-muted font-monospace">
                    {calibration.dx.toFixed(1)}, {calibration.dy.toFixed(1)}
                  </span>
                  <Form.Control
                    size="sm"
                    type="number"
                    step="0.5"
                    style={{ width: 90 }}
                    value={calibration.scale}
                    onChange={(e) =>
                      setCalibration((c) => ({ ...c, scale: Number(e.target.value) || 100 }))
                    }
                    title="Scale %"
                  />
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setCalibration(DEFAULT_CALIBRATION)}
                  >
                    Reset
                  </Button>
                  <Button variant="success" onClick={() => window.print()}>
                    Print
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                <Alert variant="info" className="py-2 small">
                  Load blank TR-9 stock in the printer, landscape, and print at
                  100% / "actual size" with page scaling off — the values are
                  positioned to land on the pre-printed rules, and any shrink-to-fit
                  will pull them off. The SD number comes from the paper.
                  {copies > 1 && (
                    <> Copies is set to {copies}, so {copies} blank forms are consumed
                    per listing; on three-part carbonless stock set it to 1, since one
                    pass already yields all three parts.</>
                  )}
                  <div className="mt-1">
                    <strong>First run on a new printer:</strong> print one form on
                    plain paper, hold it against a blank TR-9 to the light, and use
                    the nudge arrows until the values sit on the rules. The setting
                    is remembered on this computer.
                  </div>
                </Alert>

                <div ref={previewRef}>
                  {forms.map((form, i) => (
                    <div key={i} className="mb-4">
                      <div className="small text-muted mb-1">
                        Form {i + 1} of {forms.length} — {form.length} vehicle
                        {form.length === 1 ? "" : "s"}
                      </div>
                      <div
                        className="tr9-preview-frame"
                        style={{
                          width: `${PAGE.w * 96 * previewZoom}px`,
                          height: `${PAGE.h * 96 * previewZoom}px`,
                        }}
                      >
                        <div
                          style={{
                            transform: `scale(${previewZoom})`,
                            transformOrigin: "top left",
                          }}
                        >
                          <Tr9Sheet
                            form={form}
                            seller={seller}
                            purchaser={purchaser}
                            conveyDate={conveyDate}
                            calibration={calibration}
                            guide={showGuide}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      {portalNode && forms
        ? ReactDOM.createPortal(<>{sheetsForPrint}</>, portalNode)
        : null}
    </Container>
  );
}
